import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSignOutAlt, FaEllipsisV } from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { io } from "socket.io-client";
import Subscribers from "./pages/Subscribers";

import Account from "./components/Account/profile";
import AccountApprovalDashboard from "./components/AccountApproval";
import MultiStepAccount from "./components/AccountCreation/MultiStepAccount";
import AuditViewer from "./components/AuditViewer";
import CampaignApproval from "./components/CampaignApproval";
import CampaignPage from "./components/CampaignPage/CampaignPage";
import EntrepreneurCampaignSummary from "./components/CampaignSummaryCard/EntrepreneurCampaignSummary";
import CampaignList from "./components/CampaignTracker/CampaignTracker";
import CampaignViewPage from "./components/CampaignViewPage";
import Chat from "./components/chat/Chat";
import ContactForm from "./components/Contact/ContactForm";
import ChatHistoryPage from "./components/ConversationsDashboard/ChatHistoryPage";
import ConversationsDashboard from "./components/ConversationsDashboard/ConversationsDashboard";
import EmailList from "./components/EmailList";
import IdleTimer from "./components/IdleTimer";
import InvestmentTable from "./components/Investments/Investment";
import InvestorTracker from "./components/InvestorTracker/InvestorTracker";
import NotFound from "./components/NotFound";
import ServerUnavailable from "./components/ServerUnavailable";
import PublicCampaignsPage from "./components/PublicCampaignsPage";
import AccessDenied from "./components/Route/AccessDenied";
import AdminRoute from "./components/Route/AdminRoute";
import LottieSpinner from "./components/Route/LottieSpinner";
import EntrepreneurRoutes from "./components/Route/EntrepreneurRoutes";
import FaqRedirect from "./components/Route/FaqRedirect";
import InvestorRoute from "./components/Route/InvestorRoute";
import PrivateRoute from "./components/Route/PrivateRoute";
import Unauthorized from "./components/Route/Unauthorized";
import SessionManager from "./components/SessionManager/SessionManager";
import Sidebar from "./components/Sidebar";

import { showErrorToast } from "./utils/toastUtils";

import AccessKeyOverlay from "./AccessKeyOverlay";
import { refreshMenuCache } from "./api/menuApi";
import useClearToastsOnRouteChange from "./hooks/useClearToastsOnRouteChange";
import { usePageTitle } from "./hooks/usePageTitle";
import AddGroups from "./pages/addgroups";
import AddPeople from "./pages/addpeople";
import QueryList from "./pages/userQueriesList";
import AdminSEOList from "./pages/Admin_SEO_List";
import AdminCreateMenu from "./pages/AdminCreateMenu";
import AdminDashboard from "./pages/adminDashboard";
import ConfigurationSettings from "./pages/ConfigurationSettings";
import CreateUser from "./pages/createUser";
import Dashboard from "./pages/Dashboard";
import DeleteMenuTree from "./pages/DeleteMenuTree";
import DocumentCenter from "./pages/DocumentCenter";
import EntrepreneurFAQ from "./pages/EntrepreneurFAQ";
import EntrepreneurQuestions from "./pages/EntrepreneurQuestions";
import FAQ_Admin from "./pages/FAQ_Admin";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForgotUsername from "./pages/ForgotUsername";
import InvestmentConfirmationPage from "./pages/InvestmentConfirmationPage";
import InvestPage from "./pages/InvestPage";
import Login from "./pages/Login";
import NorthCapital from "./pages/NorthCapital";
import NewsPostManagement from "./pages/NewsPostManagement";
import AffiliatePartnerManagement from "./pages/AffiliatePartnerManagement";
import OrderMenuItems from "./pages/order_menuitems";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SecurityLog from "./pages/secureLog";
import Groups from "./pages/securitygroups";
import Testing from "./pages/Toggle";
import VerifyEmail from "./pages/verifyEmail";
import VerifyResetEmail from "./pages/verifyResetEmail";
import { loadConfigs } from "./redux/slices/configSlice";
import AffiliatedPartners from "./siteComponents/AffiliatedPartners";
import AQIAccount from "./siteComponents/AQIAccount";
import AQIEntrepreneurAccount from "./siteComponents/AQIEntrepreneurAccount";
import AQIHomePage from "./siteComponents/AQIHomePage";
import EntrepreneurApplication from "./siteComponents/EntrepreneurApplication";
import EntrepreneurDashboard from "./siteComponents/EntrepreneurDashboard";
import EntrepreneurLandingPage from "./siteComponents/EntrepreneurLandingPage";
import EntrepreneurRequirements from "./siteComponents/EntrepreneurRequirements";
import EntrepreneurReviewDashboard from "./siteComponents/EntrepreneurReviewDashboard";
import EntrepreneurTermsNConditions from "./siteComponents/EntrepreneurTermsNConditions";
import FAQpublic from "./siteComponents/FAQpublic";
import InvestmentLimitsPage from "./siteComponents/InvestmentLimitsPage";
import InvestorApplicationForm from "./siteComponents/InvestorApplication";
import InvestorConsent from "./siteComponents/InvestorConsent";
import InvestorDashboard from "./siteComponents/InvestorDashboard";
import InvestorReviewView from "./siteComponents/InvestorReview";
import InvestorWelcome from "./siteComponents/InvestorWelcome";
import LandingPage from "./siteComponents/LandingPage";
import Missionpg from "./siteComponents/Missionpg";
import BlogHeroSection from "./siteComponents/News_BlogPost";
import OAuthComplete from "./siteComponents/oauth-complete";
import PhoneVerificationPage from "./siteComponents/PhoneVerificationPage";
import Stories from "./siteComponents/Stories";
import SubscribeAlerts from "./siteComponents/SubscribeAlerts";
import UserUploadsPage from "./siteComponents/UserUploadsPage";
import useConfig from "../src/hooks/useConfig";

import { notifyUser } from "./utils/notifyUser";

const socket = io(process.env.REACT_APP_API_SOCKET_URL, {
    transports: ["websocket"],
});

// Axios interceptor to update localStorage when session is extended
axios.interceptors.response.use(
    (response) => {
        // Check if the response has the X-Session-Extended header
        const sessionExtended = response.headers["x-session-extended"];
        const refreshedToken = response.headers["x-session-token"];

        if(refreshedToken) {
            localStorage.setItem("session_id", refreshedToken);
        }

        if(sessionExtended) {
            // Update localStorage with the new session expiry
            localStorage.setItem("session_expiry", sessionExtended);
            localStorage.setItem("last_session_sync", Date.now().toString());

            // Dispatch event for UI components that listen for session updates
            window.dispatchEvent(
                new CustomEvent("session-synced", {
                    detail: {
                        expiry: sessionExtended,
                        reason: "extended",
                    },
                }),
            );
        }
        return response;
    },
    (error) => {
        const status = error?.response?.status;
        const backendError = error?.response?.data?.error;
        const requestUrl = error?.config?.url || "";
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const isBackendNetworkError =
            !error?.response &&
            backendUrl &&
            requestUrl.startsWith(backendUrl);
        const isAuthRoute = requestUrl.includes("/api/auth/");
        const shouldExpireSession =
            !isAuthRoute &&
            (status === 401 ||
                (status === 403 &&
                    [
                        "Invalid token",
                        "Invalid or expired token",
                        "Access denied. No token provided.",
                    ].includes(backendError)));

        if(shouldExpireSession) {
            window.dispatchEvent(
                new CustomEvent("session-expired", {
                    detail: {
                        reason: "auth-invalid",
                        status,
                        message: backendError,
                    },
                }),
            );
        }

        if(isBackendNetworkError) {
            window.dispatchEvent(new Event("backend-unavailable"));
        }

        return Promise.reject(error);
    },
);

// Public URLs that should never trigger auto-logout
const PUBLIC_ROUTES = [
    "/",
    "/login",
    "/sign-up",
    "/forgot-username",
    "/forgot-password",
    "/resetpassword",
    "/faq-public",
    "/news",
    "/blog",
    "/mission",
    "/investors",
    "/entrepreneur",
    "/entrepreneur-terms",
    "/investor-application",
    "/entrepreneur-application",
];

function App() {
    // Check if accessed via localhost and redirect to proper domain
    // Removed redirect to production for localhost
    // Now stays on localhost:3000

    // Access is persisted by the backend in an HttpOnly cookie.
    const [accessGranted, setAccessGranted] = useState(false);
    const [accessInitialized, setAccessInitialized] = useState(false);
    const [accessError, setAccessError] = useState("");
    const [accessLoading, setAccessLoading] = useState(false);
    const [backendUnavailable, setBackendUnavailable] = useState(false);
    const backendUnavailableRef = useRef(false);
    const sessionInvalidChecksRef = useRef(0);
    const lastSessionInvalidCheckRef = useRef(0);
    const { loading } = useConfig();
    const [demoMode, setDemoMode] = useState(null);
    const isInvestorsPage =
        window.location.pathname.replace(/\/+$/, "") === "/investors";

    const demoAccessUrl = `${process.env.REACT_APP_BACKEND_URL}/api/demo-access`;

    const recoverFromBackendOutage = useCallback(async() => {
        const hadAuthenticatedSession =
            localStorage.getItem("userLoggedIn") === "true";
        const recordInvalidSessionCheck = () => {
            const now = Date.now();

            if(now - lastSessionInvalidCheckRef.current >= 4 * 1000) {
                lastSessionInvalidCheckRef.current = now;
                sessionInvalidChecksRef.current += 1;
            }

            return sessionInvalidChecksRef.current >= 3;
        };

        if(hadAuthenticatedSession) {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                    {
                        credentials: "include",
                        cache: "no-store",
                    },
                );

                if(!response.ok) {
                    if(response.status !== 401 && response.status !== 403) {
                        sessionInvalidChecksRef.current = 0;
                        lastSessionInvalidCheckRef.current = 0;
                        return;
                    }

                    if(!recordInvalidSessionCheck()) return;

                    localStorage.removeItem("userLoggedIn");
                    localStorage.removeItem("session_id");
                    window.location.replace(
                        process.env.REACT_APP_FRONTEND_PUBLIC_URL || "/",
                    );
                    return;
                }

                const authenticatedUser = await response.json();
                if(!authenticatedUser?.id) {
                    if(!recordInvalidSessionCheck()) return;

                    localStorage.removeItem("userLoggedIn");
                    localStorage.removeItem("session_id");
                    window.location.replace(
                        process.env.REACT_APP_FRONTEND_PUBLIC_URL || "/",
                    );
                    return;
                }

                sessionInvalidChecksRef.current = 0;
                lastSessionInvalidCheckRef.current = 0;
            } catch{
                sessionInvalidChecksRef.current = 0;
                lastSessionInvalidCheckRef.current = 0;
                return;
            }
        }

        window.location.reload();
    }, []);

    useEffect(() => {
        const showBackendUnavailable = () => {
            backendUnavailableRef.current = true;
            setBackendUnavailable(true);
        };

        window.addEventListener(
            "backend-unavailable",
            showBackendUnavailable,
        );

        return () => {
            window.removeEventListener(
                "backend-unavailable",
                showBackendUnavailable,
            );
        };
    }, []);

    useEffect(() => {
        const loadAccessStatus = async () => {
            try {
                const response = await fetch(`${demoAccessUrl}/status`, {
                    credentials: "include",
                });

                if(!response.ok) throw new Error("Demo access status unavailable");
                const status = await response.json();

                if(backendUnavailableRef.current) {
                    await recoverFromBackendOutage();
                    return;
                }

                setBackendUnavailable(false);
                setDemoMode(Boolean(status.required));
                setAccessGranted(Boolean(status.granted));
            } catch{
                backendUnavailableRef.current = true;
                setBackendUnavailable(true);
                setDemoMode(null);
                setAccessGranted(false);
            } finally {
                setAccessInitialized(true);
            }
        };

        loadAccessStatus();

        const handleAccessRefresh = () => {
            loadAccessStatus();
        };

        const handleVisibilityChange = () => {
            if(document.visibilityState === "visible") {
                loadAccessStatus();
            }
        };

        const handlePageShow = (event) => {
            if(event.persisted) loadAccessStatus();
        };

        const accessStatusInterval = window.setInterval(
            loadAccessStatus,
            5 * 1000,
        );

        window.addEventListener("user-logged-in", handleAccessRefresh);
        window.addEventListener("demo-access-refresh", handleAccessRefresh);
        window.addEventListener("pageshow", handlePageShow);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.clearInterval(accessStatusInterval);
            window.removeEventListener("user-logged-in", handleAccessRefresh);
            window.removeEventListener("demo-access-refresh", handleAccessRefresh);
            window.removeEventListener("pageshow", handlePageShow);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [demoAccessUrl, recoverFromBackendOutage]);

    // Handler for overlay submit
    const handleAccessKey = (input) => {
        setAccessLoading(true);
        fetch(`${demoAccessUrl}/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ accessKey: input }),
        })
            .then(async (response) => {
                if(!response.ok) throw new Error("Invalid access key");
                const status = await response.json();
                setAccessGranted(Boolean(status.granted));
                setAccessError("");
            })
            .catch(() => {
                showErrorToast("Invalid access key. Please try again.");
                setAccessError("");
            })
            .finally(() => {
                setAccessLoading(false);
            });
    };
    const [userId, setUserId] = useState(null);
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const dispatch = useDispatch();

    // Keep authentication state synchronized across browser tabs. The
    // `storage` event fires in every other same-origin tab when one tab removes
    // the login flag or clears localStorage during logout.
    useEffect(() => {
        const handleCrossTabLogout = (event) => {
            const loginFlagWasRemoved =
                event.key === "userLoggedIn" && event.newValue !== "true";
            const storageWasCleared =
                event.key === null &&
                localStorage.getItem("userLoggedIn") !== "true";

            if(!loginFlagWasRemoved && !storageWasCleared) {
                return;
            }

            setUser(null);
            setUserId(null);
            setLoadingUser(false);

            if(window.location.pathname !== "/login") {
                window.location.replace("/login");
            }
        };

        window.addEventListener("storage", handleCrossTabLogout);

        return () => {
            window.removeEventListener("storage", handleCrossTabLogout);
        };
    }, []);

    // const MAX_INACTIVE_TIME = useMemo(() => {
    //     const inactiveTime = parseInt(
    //         getConfig("REACT_APP_MAX_INACTIVE_TIME") || "10",
    //         10
    //     );
    //     return inactiveTime;

    //     // Move useMemo here
    // }, [getConfig]);

    useEffect(() => {
        // Load on mount
        dispatch(loadConfigs());

        socket.on("config_updated", () => {
            // Reload on WebSocket signal
            dispatch(loadConfigs());
        });

        socket.on("news_posts_updated", () => {
            localStorage.removeItem("aqi_news_posts_manifest");
            Object.keys(localStorage)
                .filter((key) => key.startsWith("aqi_news_post:"))
                .forEach((key) => localStorage.removeItem(key));
        });

        // Clean up on unmount
        return () => {
            socket.off("config_updated");
            socket.off("news_posts_updated");
        };
    }, [dispatch]);

    useEffect(() => {
        const updateUser = async () => {
            try {
                // First check if the user is likely logged in before making API call
                const hasLoginIndicator =
                    localStorage.getItem("userLoggedIn") === "true";

                if(!hasLoginIndicator) {
                    setUser(null);
                    setUserId(null);
                    setLoadingUser(false);

                    return;
                }

                // User is likely authenticated, now fetch full user data
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                    {
                        method: "GET",
                        credentials: "include",
                    },
                );

                const data = await res.json();

                setUser(data?.id ? data : null);
                setUserId(data?.id || null);

                if(data?.id) {
                    refreshMenuCache(data.id);
                }
            } catch{
                setUser(null);
            } finally {
                setLoadingUser(false);
            }
        };
        updateUser();
    }, []);

    /* -------- listen for login event so AdminRoute sees the user immediately ------ */
    useEffect(() => {
        const handleUserLoggedIn = (e) => {
            const newUser = e.detail;

            setUser(newUser);
            setUserId(newUser.id);
            refreshMenuCache(newUser.id);
            setLoadingUser(false);
        };

        const handleSessionExpired = () => {
            localStorage.removeItem("userLoggedIn");
            localStorage.removeItem("session_id");

            setUser(null);
            setUserId(null);
            setLoadingUser(false);
            window.location.replace(
                process.env.REACT_APP_FRONTEND_PUBLIC_URL || "/",
            );
        };

        window.addEventListener("user-logged-in", handleUserLoggedIn);
        window.addEventListener("session-expired", handleSessionExpired);

        return () => {
            window.removeEventListener("user-logged-in", handleUserLoggedIn);
            window.removeEventListener("session-expired", handleSessionExpired);
        };
    }, []);

    // Fetch logged-in user from jwt token
    useEffect(() => {
        if(user) {
            socket.emit("login", user.id);

            // fetchChatHistory(user.id)
        }
    }, [user]);

    if(backendUnavailable) {
        return (
            <>
                <ToastContainer />
                <ServerUnavailable
                    onRetry={async() => {
                        try {
                            const response = await fetch(
                                `${demoAccessUrl}/status`,
                                {
                                    credentials: "include",
                                    cache: "no-store",
                                },
                            );

                            if(response.ok) {
                                await recoverFromBackendOutage();
                            }
                        } catch{
                            // Keep showing the unavailable page until the backend recovers.
                        }
                    }}
                />
            </>
        );
    }

    return (
        <>
            <ToastContainer />
            {(loading || !accessInitialized) && !isInvestorsPage && (
                <LottieSpinner />
            )}
            {!accessGranted && demoMode === true && accessInitialized && (
                <AccessKeyOverlay
                    onAccessGranted={handleAccessKey}
                    error={accessError}
                    loading={accessLoading}
                />
            )}
            {(isInvestorsPage ||
                (accessInitialized &&
                    (accessGranted || demoMode === false))) && (
                <Router>
                    <MainLayout
                        userId={userId}
                        user={user}
                        setUser={setUser}
                        loadingUser={loadingUser}
                    />
                </Router>
            )}
        </>
    );
}

function MainLayout({ userId, loadingUser, user, setUser }) {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const handleForceLogout = () => {
            localStorage.removeItem("userLoggedIn");
            localStorage.removeItem("session_expiry");
            localStorage.clear();

            if(socket && socket.connected) {
                socket.disconnect();
            }

            notifyUser(null, "error", "You have been logged out by admin.", "toast", null, null, null, null, null, false, false, "top-center");

            navigate("/login");
        };

        socket.on("force_logout", handleForceLogout);

        return () => {
            socket.off("force_logout", handleForceLogout);
        };
    }, [navigate]);

    // Automatically update page titles based on current route
    usePageTitle();

    // Use the hook to automatically clear toasts on route changes
    useClearToastsOnRouteChange();

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);

        const verifyBackendConnection = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/demo-access/status`,
                    { credentials: "include" },
                );

                if(response.status >= 500) {
                    window.dispatchEvent(new Event("backend-unavailable"));
                }
            } catch{
                window.dispatchEvent(new Event("backend-unavailable"));
            }
        };

        verifyBackendConnection();
    }, [location.pathname]);

    // Inactivity timeout setup (only for authenticated users on protected routes)
    React.useEffect(() => {
        let inactivityTimer;

        // Skip everything if the visitor is NOT logged-in
        if(!user?.id) return;

        // Skip while the user is on a public route
        const isPublic = PUBLIC_ROUTES.some(
            (r) =>
                location.pathname === r ||
                location.pathname.startsWith(r + "/"),
        );

        if(isPublic) return;

        // Logout user when clicking on logout button
        const logoutUser = async () => {
            const userObj = JSON.parse(localStorage.getItem("user"));

            const inactiveMinutes =
                typeof window.MAX_INACTIVE_TIME !== "undefined"
                    ? window.MAX_INACTIVE_TIME
                    : 10;

            if(userObj?.id) {
                try {
                    await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/security_log/inactivity-logout`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                userId: userObj.id,
                                eventType: "inactivity_logout",
                                eventResult: "success",
                                reason: `User inactive for ${inactiveMinutes} minute${
                                    inactiveMinutes > 10 ? "s" : ""
                                }`,
                            }),
                        },
                    );
                } catch(error) {
                    console.error("Failed to log inactivity logout:", error);
                }
            }

            // Specifically remove login indicator first
            localStorage.removeItem("userLoggedIn");

            // Then clear all remaining localStorage
            localStorage.clear();
            window.location.href = "/login";
        };

        // Try to get MAX_INACTIVE_TIME from props or fallback
        const timeoutMinutes =
            typeof window.MAX_INACTIVE_TIME !== "undefined"
                ? window.MAX_INACTIVE_TIME
                : 10;

        const timeoutDuration = timeoutMinutes * 60 * 1000;

        const resetTimer = () => {
            clearTimeout(inactivityTimer);
            inactivityTimer = setTimeout(logoutUser, timeoutDuration);
        };

        window.addEventListener("mousemove", resetTimer);
        window.addEventListener("keydown", resetTimer);
        window.addEventListener("click", resetTimer);

        resetTimer();

        return () => {
            clearTimeout(inactivityTimer);
            window.removeEventListener("mousemove", resetTimer);
            window.removeEventListener("keydown", resetTimer);
            window.removeEventListener("click", resetTimer);
        };
    }, [user, location.pathname]);

    const isInvestorAccountPage =
        location.pathname === "/investor-account-creation";
    const isSubmitted =
        user?.submitted === true || user?.application_submitted === 1;
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Simplified is404Route logic
    const is404Route = location.pathname.startsWith("/404-");

    // clear flag on every route change
    useEffect(() => {
        // Remove hidesidebar flag when navigating to any route
        localStorage.removeItem("hideSidebar");
    }, [location.pathname]);

    // Hiding siding bar for the public pages
    const hideSidebarRoutes = [
        "/",
        "/login",
        "/sign-up",
        "/landing-page",
        "/unauthorized",
        "/investor-application",
        "/investor-account-creation",
        "/investor-account-creation-old",
        "/phone-verification",
        "/investment-limits",
        "/entrepreneur-application",
        "/entrepreneur-account-creation",
        "/entrepreneur-requirements",
        "/forgot-username",
        "/forgot-password",
        "/resetpassword",
        "/home1",
        "/home2",
        "/news",
        "/investor-team",
        "/faq-public",
        "/mission",
        "/news",
        "/resources",
        "/faq/public/investors",
        "/faq/public/entrepreneurs",
        "/faq/public/glossary",
        "/entrepreneurs",
        "/investors",
        "/campaigns/view",
            "/404",
        "/entrepreneur-terms",
        "/invest",
        "/payment-success",
        "/contact-us",
        "/subscribe-alerts",
        "/investment-confirmation",
    ];

    const isSidebarHiddenRoute = hideSidebarRoutes.some(
        (route) =>
            location.pathname === route ||
            location.pathname.startsWith(route + "/"),
    );

    // Prevent sidebar flash even on protected routes like "/dashboard"
    const showSidebar =
        (!isSidebarHiddenRoute || (isInvestorAccountPage && isSubmitted)) &&
        !is404Route &&
        user;

    const handleLogout = async () => {
        try {
            const userId = user?.id;
            const email = user?.email || "N/A";

            if(userId) {
                await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/security_log/logout`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            userId,
                            email,
                            eventType: "logout",
                            eventResult: "success",
                            reason: "User logged out",
                        }),
                    },
                );
            }

            await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`,
                {
                    method: "POST",
                    credentials: "include",
                },
            );

            // Clear the login indicator to prevent unnecessary API calls
            localStorage.removeItem("userLoggedIn");
        } catch(err) {
            console.error("Logout log failed:", err);

            await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/security_log/logout`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: user?.id || null,
                        email: user?.email || "N/A",
                        eventType: "logout",
                        eventResult: "failure",
                        reason: `Logout logging failed: ${err.message}`,
                    }),
                },
            );
        }

        navigate("/login");
    };

    useEffect(() => {
        const path = location.pathname;

        if(user?.role === "admin") {
            const is404 = path === "/404";
            const previousPath =
                document.referrer.split(window.location.origin)[1] || "Unknown";

            const reason = is404
                ? `404 Not Found - ${previousPath}`
                : `Navigated to ${decodeURIComponent(
                      path.replace("/", "") || "HOME",
                  ).toLowerCase()}`;

            const eventResult = is404 ? "failure" : "success";

            fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/security_log/navigation`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: user.id,
                        eventType: "Privileged Page Access",
                        eventResult,
                        email: user.email,
                        reason,
                    }),
                },
            ).catch((err) => console.error("Log failed:", err));
        }
    }, [location.pathname, user]);

    // Track total unread message count
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);

    // Fetch unread message count from backend
    const fetchUnreadCount = async () => {
        if(!user?.id) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count/${user.id}`,
                {
                    credentials: "include",
                },
            );

            const data = await response.json();

            setUnreadMessageCount(data.unreadCount || 0);
        } catch(error) {
            console.error("Error fetching unread count:", error);
        }
    };

    // Fetch unread count when user changes and periodically
    useEffect(() => {
        if(user?.id) {
            fetchUnreadCount();

            // Set up periodic refresh of unread count every 30 seconds
            const interval = setInterval(fetchUnreadCount, 30000);

            return () => clearInterval(interval);
        } else {
            setUnreadMessageCount(0);
        }
    }, [user]);

    // Function to send toast notificaton when message received

    useEffect(() => {
        if(!user?.id) return;

        const handleGlobalReceive = (newMessage) => {
            // Ignore our own messages
            if(newMessage.senderId === user.id) return;

            // If Chat page currently has this sender open, skip duplicate toast
            const openChatId = window.currentChatUserId || null;

            if(openChatId && newMessage.senderId === openChatId) return;

            // Show toast notification
            notifyUser(`global-missed-${newMessage.senderId}-${Date.now()}`, "info", "You have a new message", "toast", null, null, 5000, null, null, false, false, "top-center");

            // Increment unread message count
            setUnreadMessageCount((prev) => prev + 1);
        };

        socket.on("receive_message", handleGlobalReceive);

        // Listen for message_seen events to update count
        const handleMessageSeen = () => {
            // Refresh unread count when messages are marked as seen
            fetchUnreadCount();
        };

        socket.on("message_seen", handleMessageSeen);

        return () => {
            socket.off("receive_message", handleGlobalReceive);
            socket.off("message_seen", handleMessageSeen);
        };
    }, [user]);

    return (
        <>
            {/* Global Logout + Quick Action Buttons */}
            {showSidebar && (
                <div className="fixed top-0 left-0 right-0 h-16 z-50 pointer-events-none">
                    {/* Desktop View - Show individual buttons */}
                    <div className="hidden lg:block pointer-events-auto">
                        {/* Header background with shadow line - perfectly aligned with sidebar */}
                        <div
                            className={`fixed top-0 right-0 h-20 bg-white shadow-md z-40 transition-all duration-300 ${
                                isSidebarOpen ? "left-64" : "left-16"
                            }`}
                        ></div>

                        {/* Logout */}
                        <div className="fixed top-4 right-4 z-50">
                            <button
                                onClick={handleLogout}
                                title="Logout"
                                className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow hover:bg-red-100 transition"
                            >
                                <FaSignOutAlt
                                    className="text-red-600"
                                    size={20}
                                />
                            </button>
                        </div>

                        {/* Account, Notification & Support Icons */}
                        {user && (
                            <div className="fixed top-4 right-20 z-50 flex space-x-3">
                                {/* Account */}
                                <button
                                    onClick={() => navigate("/Account")}
                                    title="Account"
                                    className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow hover:bg-purple-100 transition"
                                >
                                    <i className="fas fa-user text-navy text-lg"></i>
                                </button>

                                {/* Notification */}
                                <button
                                    onClick={() => {
                                        navigate("/chat");
                                        // Reset unread count when opening chat
                                        setUnreadMessageCount(0);
                                    }}
                                    title={`Notifications${
                                        unreadMessageCount > 0
                                            ? ` (${unreadMessageCount} unread Message${
                                                  unreadMessageCount > 1
                                                      ? "s"
                                                      : ""
                                              })`
                                            : ""
                                    }`}
                                    className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow hover:bg-yellow-100 transition relative"
                                >
                                    <i className="fas fa-bell text-yellow-500 text-lg"></i>
                                    {unreadMessageCount > 0 && (
                                        <span
                                            className="absolute -top-2 right-0 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 shadow-md"
                                            style={{
                                                minWidth: "1.5rem",
                                                textAlign: "center",
                                            }}
                                        >
                                            {unreadMessageCount}
                                        </span>
                                    )}
                                </button>

                                {/* Support */}
                                {(user?.role === "investor" ||
                                    user?.role === "entrepreneur") && (
                                    <button
                                        onClick={() => navigate("/contact")}
                                        title="Support"
                                        className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow hover:bg-purple-100 transition"
                                    >
                                        <i className="fas fa-headset text-navy text-lg"></i>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile View - Three dots menu */}
                    <div className="lg:hidden">
                        {/* Mobile header background */}
                        <div
                            className={`fixed top-0 right-0 h-20 bg-white shadow-md z-40 transition-all duration-300 ${
                                showSidebar ? "left-14" : "left-0"
                            }`}
                        ></div>

                        <div className="fixed top-4 right-4 z-50">
                            <div className="relative">
                                <button
                                    onClick={() =>
                                        setIsMobileMenuOpen(!isMobileMenuOpen)
                                    }
                                    title="Menu"
                                    className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow hover:bg-gray-100 transition"
                                >
                                    <FaEllipsisV
                                        className="text-navy"
                                        size={18}
                                    />
                                </button>

                                {/* Mobile Dropdown Menu */}
                                {isMobileMenuOpen && (
                                    <>
                                        {/* Backdrop */}
                                        <div
                                            className="fixed inset-0 bg-black bg-opacity-25 z-40"
                                            onClick={() =>
                                                setIsMobileMenuOpen(false)
                                            }
                                        />

                                        {/* Dropdown */}
                                        <div className="absolute right-0 top-14 w-48 bg-white rounded-lg shadow-lg border z-50 py-2 mobile-menu-enter">
                                            {/* Account Option */}
                                            {user && (
                                                <button
                                                    onClick={() => {
                                                        navigate("/Account");
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        );
                                                    }}
                                                    className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                                >
                                                    <i className="fas fa-user text-navy text-lg mr-3"></i>
                                                    <span className="text-gray-700 font-medium">
                                                        Account
                                                    </span>
                                                </button>
                                            )}

                                            {/* Notification Option */}
                                            {user && (
                                                <button
                                                    onClick={() => {
                                                        navigate("/chat");
                                                        setUnreadMessageCount(
                                                            0,
                                                        );
                                                        // Reset unread count when opening chat
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        );
                                                    }}
                                                    className="flex items-center w-full px-4 py-3 text-left hover:bg-yellow-50 transition-colors relative"
                                                >
                                                    <i className="fas fa-bell text-yellow-500 text-lg mr-3"></i>
                                                    <span className="text-gray-700 font-medium">
                                                        Notifications
                                                    </span>
                                                    {unreadMessageCount > 0 && (
                                                        <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                                                            {unreadMessageCount}
                                                        </span>
                                                    )}
                                                </button>
                                            )}

                                            {/* Support Option */}
                                            {(user?.role === "investor" ||
                                                user?.role ===
                                                    "entrepreneur") && (
                                                <button
                                                    onClick={() => {
                                                        navigate("/contact");
                                                        setIsMobileMenuOpen(
                                                            false,
                                                        );
                                                    }}
                                                    className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                                                >
                                                    <i className="fas fa-headset text-navy text-lg mr-3"></i>
                                                    <span className="text-gray-700 font-medium">
                                                        Support
                                                    </span>
                                                </button>
                                            )}

                                            {/* Divider */}
                                            <div className="border-t border-gray-100 my-1"></div>

                                            {/* Logout Option */}
                                            <button
                                                onClick={() => {
                                                    handleLogout();
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                className="flex items-center w-full px-4 py-3 text-left hover:bg-red-50 transition-colors"
                                            >
                                                <FaSignOutAlt className="text-red-600 text-lg mr-3" />
                                                <span className="text-red-600 font-medium">
                                                    Logout
                                                </span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            {showSidebar && (
                <div
                    className={`fixed top-0 left-0 h-screen ${
                        isSidebarOpen
                            ? "w-48 sm:w-56 lg:w-64"
                            : "w-12 sm:w-14 lg:w-16"
                    } transition-all duration-300`}
                >
                    <Sidebar
                        userId={userId}
                        user={user}
                        isSidebarOpen={isSidebarOpen}
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                </div>
            )}

            {/* Main Content Area */}
            <div
                className={`flex-1 overflow-y-auto transition-all duration-300 ${
                    showSidebar
                        ? isSidebarOpen
                            ? "pl-48 sm:pl-56 lg:pl-64"
                            : "pl-12 sm:pl-14 lg:pl-16"
                        : ""
                } `}
            >
                {/* Header space for icons - ensures content doesn't overlap with icons */}
                {showSidebar && <div className="h-[70px] w-full"></div>}
                <Routes>
                    {/*  Public Routes */}
                    <Route path="/404" element={<NotFound />} />
                    <Route path="/access-denied" element={<AccessDenied />} />
                    <Route path="/" element={<AQIHomePage />} />
                    <Route path="/" element={<Navigate to="/" replace />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="/sign-up" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route
                        path="/forgot-username"
                        element={<ForgotUsername />}
                    />
                    <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                    />
                    <Route path="/resetpassword" element={<ResetPassword />} />
                    <Route path="/mission" element={<Missionpg />} />
                    <Route path="/news" element={<Stories />} />
                    <Route path="/faq-public" element={<FAQpublic />} />
                    <Route
                        path="/faq/public/investors"
                        element={<FAQpublic />}
                    />
                    <Route
                        path="/faq/public/entrepreneurs"
                        element={<FAQpublic />}
                    />
                    <Route
                        path="/faq/public/glossary"
                        element={<FAQpublic />}
                    />
                    <Route
                        path="/faq/public/general"
                        element={<FAQpublic />}
                    />
                    <Route path="/resources" element={<AffiliatedPartners />} />
                    <Route
                        path="/investors"
                        element={<PublicCampaignsPage />}
                    />
                    <Route
                        path="/startups"
                        element={<PublicCampaignsPage initialViewMode="table" />}
                    />
                    <Route
                        path="/campaigns/view/:campaignId"
                        element={<CampaignViewPage user={user} />}
                    />
                    <Route
                        path="/investor-application"
                        element={<InvestorApplicationForm />}
                    />
                    <Route
                        path="/entrepreneur-application"
                        element={<EntrepreneurApplication />}
                    />
                    <Route
                        path="/entrepreneur-requirements"
                        element={<EntrepreneurRequirements />}
                    />
                    <Route
                        path="/entrepreneur-terms"
                        element={
                            <EntrepreneurTermsNConditions
                                onBack={() => navigate("/entrepreneurs")}
                                onSuccess={() =>
                                    navigate("/entrepreneur-application")
                                }
                            />
                        }
                    />
                    <Route
                        path="/campaigns/:campaignId"
                        element={<CampaignViewPage user={user} />}
                    />
                    <Route
                        path="/oauth-complete"
                        element={<OAuthComplete user={user} />}
                        user={user}
                        loadingUser={loadingUser}
                    />
                    <Route
                        path="/entrepreneurs"
                        element={<EntrepreneurLandingPage />}
                    />
                    <Route path="/contact-us" element={<ContactForm />} />
                    <Route path="/blog/:id" element={<BlogHeroSection />} />
                    <Route
                        path="/verify-emailPassword"
                        element={<VerifyEmail />}
                    />
                    <Route
                        path="/reset-password"
                        element={<VerifyResetEmail />}
                    />
                    <Route
                        path="/reset-passwordPage"
                        element={<ResetPasswordPage />}
                    />
                    <Route
                        path="/subscribe-alerts"
                        element={<SubscribeAlerts />}
                    />

                    {/*  Investor Routes */}
                    <Route
                        path="/investments"
                        element={
                            <InvestorRoute
                                element={<InvestmentTable user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Investor-tracker"
                        element={
                            <InvestorRoute
                                element={<InvestorTracker user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/investor-dashboard"
                        element={
                            <InvestorRoute
                                element={<InvestorDashboard user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/investor-review"
                        element={
                            <InvestorRoute
                                element={<InvestorReviewView />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/investor-account-creation"
                        element={
                            <MultiStepAccount
                                user={user}
                                setUser={setUser}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/investor-account-creation-old"
                        element={
                            <AQIAccount user={user} loadingUser={loadingUser} />
                        }
                    />
                    <Route
                        path="/investor-welcome"
                        element={<InvestorWelcome />}
                    />
                    <Route
                        path="/phone-verification"
                        element={<PhoneVerificationPage />}
                    />
                    <Route
                        path="/investment-limits"
                        element={<InvestmentLimitsPage />}
                    />
                    <Route
                        path="/investor-application"
                        element={<InvestorApplicationForm />}
                    />
                    <Route
                        path="/investor-consent"
                        element={<InvestorConsent />}
                    />
                    <Route
                        path="/invest"
                        element={
                            <InvestorRoute
                                element={<InvestPage user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/investment-confirmation"
                        element={
                            <InvestorRoute
                                element={
                                    <InvestmentConfirmationPage user={user} />
                                }
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/payment-success"
                        element={
                            <InvestorRoute
                                element={<PaymentSuccessPage />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />

                    {/*  entrepreneur Routes */}
                    <Route
                        path="/entrepreneur-dashboard/account-review"
                        element={
                            <EntrepreneurRoutes
                                element={<AQIEntrepreneurAccount user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Campaign-tracker"
                        element={
                            <EntrepreneurRoutes
                                element={<CampaignList user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/campaign-page"
                        element={
                            <EntrepreneurRoutes
                                element={<CampaignPage user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/campaign-summary"
                        element={
                            <EntrepreneurRoutes
                                element={
                                    <EntrepreneurCampaignSummary user={user} />
                                }
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/entrepreneur-account-creation"
                        element={
                            <AQIEntrepreneurAccount
                                user={user}
                                setUser={setUser}
                                loadingUser={loadingUser}
                            />
                        }
                    />

                    {/*  Private Routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute
                                element={<Dashboard user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/home"
                        element={
                            <PrivateRoute
                                element={<Dashboard user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/chat"
                        element={
                            <PrivateRoute
                                element={
                                    <Chat
                                        user={user}
                                        unreadMessageCount={unreadMessageCount}
                                        setUnreadMessageCount={
                                            setUnreadMessageCount
                                        }
                                        fetchUnreadCount={fetchUnreadCount}
                                    />
                                }
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/chat-history/:founder_id/:investor_id"
                        element={
                            <PrivateRoute
                                element={<ChatHistoryPage />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route path="/faq" element={<FaqRedirect user={user} />} />
                    <Route
                        path="/dashboard/faq/investors"
                        element={
                            <PrivateRoute
                                element={<FAQpublic />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/dashboard/faq/entrepreneurs"
                        element={
                            <PrivateRoute
                                element={<FAQpublic />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/dashboard/faq/glossary"
                        element={
                            <PrivateRoute
                                element={<FAQpublic />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/contact"
                        element={
                            <PrivateRoute
                                element={<ContactForm user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/entrepreneur-review-dashboard"
                        element={
                            <PrivateRoute
                                element={
                                    <EntrepreneurReviewDashboard user={user} />
                                }
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/entrepreneur-dashboard"
                        element={
                            <PrivateRoute
                                element={<EntrepreneurDashboard user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/entrepreneur-questions"
                        element={
                            <PrivateRoute
                                element={<EntrepreneurQuestions user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/entrepreneur-faq"
                        element={<PrivateRoute element={<EntrepreneurFAQ />} />}
                    />
                    <Route
                        path="/support"
                        element={
                            <PrivateRoute
                                element={<QueryList />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Account"
                        element={
                            <PrivateRoute
                                element={<Account user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Documents-center"
                        element={
                            user?.role === "admin" ? (
                                <DocumentCenter />
                            ) : (
                                <PrivateRoute
                                    element={
                                        <UserUploadsPage
                                            userId={userId}
                                            user={user}
                                        />
                                    }
                                    user={user}
                                    loadingUser={loadingUser}
                                />
                            )
                        }
                    />
                    <Route
                        path="/north-capital"
                        element={
                            <AdminRoute
                                element={<NorthCapital />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    {/*   Admin Routes */}
                    <Route
                        path="/Menu-items"
                        element={
                            <AdminRoute
                                element={<Testing user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Security-groups"
                        element={
                            <AdminRoute
                                element={<Groups user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/addpeople/:groupId"
                        element={
                            <AdminRoute
                                element={<AddPeople user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/addpages/:groupId"
                        element={
                            <AdminRoute
                                element={<AddGroups />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Chat-logs"
                        element={
                            <AdminRoute
                                element={<ConversationsDashboard />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/User-sessions"
                        element={
                            <AdminRoute
                                element={<SessionManager user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Users"
                        element={
                            <AdminRoute
                                element={<AdminDashboard />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/SEO-management"
                        element={
                            <AdminRoute
                                element={<AdminSEOList />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/create-user"
                        element={
                            <AdminRoute
                                element={<CreateUser />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/admin/create-menu"
                        element={
                            <AdminRoute
                                element={
                                    <AdminCreateMenu
                                        userId={userId}
                                        loadingUser={loadingUser}
                                    />
                                }
                                user={user}
                            />
                        }
                    />
                    <Route
                        path="/admin/document-center"
                        element={
                            <AdminRoute
                                element={<DocumentCenter />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/admin/order-menuitems"
                        element={
                            <AdminRoute
                                element={<OrderMenuItems />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/admin/delete-menu"
                        element={
                            <AdminRoute
                                element={<DeleteMenuTree />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Mail-queue"
                        element={
                            <AdminRoute
                                element={<EmailList />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Change-log"
                        element={
                            <AdminRoute
                                element={<AuditViewer />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Security-log"
                        element={
                            <AdminRoute
                                element={<SecurityLog />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/FAQ-management"
                        element={
                            <AdminRoute
                                element={<FAQ_Admin />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/BlogPost-management"
                        element={
                            <AdminRoute
                                element={<NewsPostManagement />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Affiliates-management"
                        element={
                            <AdminRoute
                                element={<AffiliatePartnerManagement />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/Config-params"
                        element={
                            <AdminRoute
                                element={<ConfigurationSettings />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/applications"
                        element={
                            <AdminRoute
                                element={
                                    <AccountApprovalDashboard user={user} />
                                }
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="/campaigns"
                        element={
                            <AdminRoute
                                element={<CampaignApproval user={user} />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />

                    <Route
                        path="/subscribers"
                        element={
                            <AdminRoute
                                element={<Subscribers />}
                                user={user}
                                loadingUser={loadingUser}
                            />
                        }
                    />
                    <Route
                        path="*"
                        element={<Navigate to="/404" />}
                    />
                </Routes>
                {user && <IdleTimer />}
            </div>
        </>
    );
}

MainLayout.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loadingUser: PropTypes.bool,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
        submitted: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
        application_submitted: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
    }),
    setUser: PropTypes.func,
};

App.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        role: PropTypes.string,
        submitted: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
        application_submitted: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),
    }),
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setUser: PropTypes.func,
    loadingUser: PropTypes.bool,
};

export default App;
